import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import WalletProp, { WALLET_TYPE } from '../../proptype/walletProp';
import translate from '../../../../commonComponents/translate';
import { newFormatPrice } from '../../../../services/utilityFunctions';
import WalletIcon from './icons/WalletIcon';
import CoinsIcon from './icons/CoinsIcon';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  formatDecimalPrices,
  formatNumber,
  isSmallerThanTwoDecimal,
} from '../../../../services/numberUtilis';

function CommissionsWalletCardListItem(props) {
  const { t, wallet } = props;

  const { organization } = useContext(ConfigsContext);
  const { enabled: isCharityCoinsEnabled } =
    organization?.services?.charity || {};

  const {
    type,
    currencyInfo,
    balance,
    userPriceBalance,
    available: walletAvailable,
    userPriceAvailable,
    coinsAvailable,
    charityCoinsAvailable,
  } = wallet;

  const isCoinsWallet = type === WALLET_TYPE.COINS;
  const showExtraInformation =
    isCoinsWallet && userPriceAvailable && userPriceBalance;

  const icon = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return <img src={currencyInfo.icon} alt="crypto logo" />;
      case WALLET_TYPE.FIAT:
        return <WalletIcon />;
      case WALLET_TYPE.COINS:
        return <CoinsIcon />;
      default:
        return null;
    }
  }, [type, currencyInfo]);

  const iconClassWrapper = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.COINS:
        return 'commissions-wallet-picker-card-header-icon commissions-wallet-picker-card-header-icon-coins';
      default:
        return 'commissions-wallet-picker-card-header-icon';
    }
  }, [type]);

  const walletName = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.CRYPTO:
        return currencyInfo?.name;
      default:
        return t(`commissions.walletType.${type}`);
    }
  }, [type]);

  const cryptoCurrency = useMemo(
    () => currencyInfo?.symbol_native || currencyInfo?.symbol,
    [currencyInfo],
  );

  const formatValue = (value, code) =>
    isSmallerThanTwoDecimal(Math.floor(value) || 0)
      ? formatDecimalPrices(Math.floor(value) || 0, true, code)
      : newFormatPrice(Math.floor(value) || 0, true, code, true);

  const totalBalanceTxt = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.COINS:
        return userPriceBalance
          ? t('commissions.totalBalance')
          : t('rewards_coins');
      default:
        return t('commissions.totalBalance');
    }
  }, [type]);
  const totalBalance = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return formatValue(userPriceBalance?.amount, userPriceBalance?.code);
      case WALLET_TYPE.CRYPTO:
        return `${balance || 0}${cryptoCurrency}`;
      case WALLET_TYPE.COINS:
        return userPriceBalance
          ? formatValue(userPriceBalance?.amount, userPriceBalance?.code)
          : balance || 0;
      default:
        return balance || 0;
    }
  }, [type, balance, cryptoCurrency, userPriceBalance]);

  const available = useMemo(() => {
    switch (type) {
      case WALLET_TYPE.FIAT:
        return formatValue(
          userPriceAvailable?.amount,
          userPriceAvailable?.code,
        );
      case WALLET_TYPE.CRYPTO:
        return `${walletAvailable || 0}${cryptoCurrency}`;
      case WALLET_TYPE.COINS:
        return userPriceAvailable
          ? formatValue(userPriceAvailable?.amount, userPriceAvailable?.code)
          : walletAvailable || 0;
      default:
        return walletAvailable || 0;
    }
  }, [type, walletAvailable, cryptoCurrency, userPriceAvailable]);

  return (
    <div className="commissions-wallet-picker-card-layer">
      <div className="commissions-wallet-picker-card">
        <div className="commissions-wallet-picker-card-header">
          <div className={iconClassWrapper}>{icon}</div>
          <div className="commissions-wallet-picker-card-header-title">
            {walletName}
          </div>
        </div>
        <div className="commissions-wallet-picker-card-content">
          <div className="commissions-wallet-picker-card-content-label">
            {totalBalanceTxt}:
          </div>
          <div className="commissions-wallet-picker-card-content-total-balance">
            {totalBalance}
          </div>
          <div className="commissions-wallet-picker-card-extra-info">
            {!isCoinsWallet ? (
              <div className="info">
                <div className="commissions-wallet-picker-card-content-label">
                  {t('commissions.available')}:
                </div>
                <div className="commissions-wallet-picker-card-content-available">
                  {available}
                </div>
              </div>
            ) : null}
            {showExtraInformation ? (
              <>
                <div className="info">
                  <div className="commissions-wallet-picker-card-content-label">
                    {t('rewards_coins')}:
                  </div>
                  <div className="commissions-wallet-picker-card-content-available">
                    {formatNumber(coinsAvailable)}
                  </div>
                </div>
                {isCharityCoinsEnabled ? (
                  <div className="info">
                    <div className="commissions-wallet-picker-card-content-label">
                      {t('rewards_charityCoins')}:
                    </div>
                    <div className="commissions-wallet-picker-card-content-available">
                      {formatNumber(charityCoinsAvailable)}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

CommissionsWalletCardListItem.propTypes = {
  t: PropTypes.func.isRequired,
  wallet: PropTypes.shape(WalletProp.propType).isRequired,
};

export default translate(CommissionsWalletCardListItem);
